import { inject, Injectable } from "@angular/core";
import { HTTP_INTERCEPTORS, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { catchError, finalize, Observable, shareReplay, switchMap, tap, throwError } from "rxjs";
import { Store } from "@ngrx/store";

import { HttpStatusCodes } from "../core";
import { ZefHttpErrorResponse } from "../errors";
import { TokenData } from "./auth.model";
import { ZefAuthApi } from './auth.api';
import { AuthTokenStorageService } from "./auth.utils";
import { zefRefreshToken, zefRefreshTokenFail, zefRefreshTokenSuccess } from "./refresh-token.action";

@Injectable()
export class AuthAndRefreshTokenInterceptor implements HttpInterceptor {

  #refreshToken$?: Observable<TokenData>;

  #api = inject(ZefAuthApi);
  #store = inject(Store);
  #tokenService = inject(AuthTokenStorageService);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    const tokenData = this.#tokenService.getToken();
    const accessToken = tokenData?.accessToken;

    return next.handle(this.addToken(req, accessToken)).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status !== HttpStatusCodes.UNAUTHORIZED) {
          return throwError(() => err);
        }
        if (!tokenData?.refreshToken) {
          return throwError(() => err);
        }
        return this.handle401(req, next);
      })
    );
  }

  private handle401(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    /**
     * If a refresh is already in progress, all calls will wait for the same observable
     * to complete, ensuring only one refresh request is made at a time.
     */
    if (!this.#refreshToken$) {

      const refreshToken = this.#tokenService.getToken()?.refreshToken;
      if (!refreshToken) {
        return throwError(() => new Error("The refresh token is not available!"));
      }

      const refreshTokenAction = zefRefreshToken(undefined, { type: 'dialog' });
      this.#store.dispatch(refreshTokenAction);

      this.#refreshToken$ = this.#api.refresh$<TokenData>(refreshToken).pipe(
        tap(tokenData => {
          this.#tokenService.setToken(tokenData);
          this.#store.dispatch(zefRefreshTokenSuccess(tokenData, refreshTokenAction));
        }),
        catchError((innerErr: ZefHttpErrorResponse) => {
          this.#store.dispatch(zefRefreshTokenFail(innerErr, refreshTokenAction));
          return throwError(() => innerErr);
        }),
        finalize(() => {
          /**
           * Reset the refresh observable here, after the refresh attempt completes (whether it succeeds or fails).
           * This ensures that if multiple requests fail with 401 while a refresh is in progress, they will all wait
           * for the same refresh attempt to complete before any new refresh attempts can be initiated.
           */
          this.#refreshToken$ = undefined;
        }),
        shareReplay(1)
      );
    }

    /**
     * We don't need to handle errors here because if the refresh fails, the error will be propagated to all waiting callers.
     */
    return this.#refreshToken$.pipe(
      switchMap(tokenData => {
        return next.handle(this.addToken(req, tokenData.accessToken));
      })
    );
  }

  private addToken(req: HttpRequest<any>, accessToken?: string): HttpRequest<any> {
    if (req.headers.get('zefSkipAuthHeader')) {
      return req.clone({ headers: req.headers.delete('zefSkipAuthHeader') });
    }
    if (!accessToken) {
      return req;
    }
    return req.clone({
      setHeaders: { Authorization: `Bearer ${accessToken}` }
    });
  }
}

export const authTokenInterceptorProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: AuthAndRefreshTokenInterceptor,
  multi: true
};
